import { COLLECTIONS, MUTATE_OPERATIONS_SENT } from "constants/action-types";
import { ActionsObservable, Epic } from "redux-observable";
import { IStore } from "reducers";
import { TARGETS_ID, TARGET_BAG_ID } from "constants/product-collection";
import { List, Map } from "immutable";
import { MutableType, OperationType, bulkMixedSetOperation } from "actions/mutate";
import { getAndIncrementRequestId } from "./helpers";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import {
    RemoveTargetPayload,
    SaveTargetObjectsPayload,
    TargetsQueueType,
    clearTargetsQueueAction,
    createTargetsQueue,
    executeTargetsQueueAction,
    shiftTargetsQueueAction,
    syncTargetGroupsCompletedAction,
} from "actions/collections/collection-target";
import { Action } from "redux";
import {
    BAG_VALUE_PARAMS_SEPARATOR,
    createBatchMutations,
    getAllocationBagValuePath,
} from "helpers/allocation-dimension";
import { getAllocationBagMutationPayload } from "./helpers/getAllocationBagMutationPayload";
import { ICollectionState } from "reducers/collection";
import { IMutateAllocationBagValue } from "actions/collections";
import {
    convertTargetGroupToMutations,
    createGhostTargetSetOperations,
    generateTargetRemoveMutations,
    handleTargetGroupsSyncError,
    mapOperations,
} from "./helpers/target";
import { snackbarAdd } from "actions/snackbars";
import { format } from "lib/globalize-util";
import { getTargetGroupsWithTargets } from "reducers/selectors/collection";
import { updateSelectedTargetIds } from "actions/financial-target/actions";

type RemoveTargetObservableAction = ActionsObservable<{
    payload: RemoveTargetPayload;
    type: typeof COLLECTIONS.COLLECTION.TARGET_METRICS.REMOVE;
}>;

type TargetGroupObservableAction = ActionsObservable<{
    payload: { objectId: string; syncData: boolean };
    type: typeof COLLECTIONS.COLLECTION.TARGET_METRICS.TARGET_GROUPS.SYNC.START;
}>;

export const collectionTargetGroupsSyncEpic: Epic<Action, IStore> = (action$: TargetGroupObservableAction, store) => {
    return (
        action$
            .ofType(COLLECTIONS.COLLECTION.TARGET_METRICS.TARGET_GROUPS.SYNC.START)
            // Dont proceed if there is something sending or there is nothing to send
            .filter(({ payload }) => {
                const { mutate } = store.getState();
                const { sentMutations, uncommittedMutations } = mutate;
                const sentMutation = sentMutations.get(payload.objectId);
                const uncommittedMutation = uncommittedMutations.get(payload.objectId) || List();

                return !sentMutation && !!uncommittedMutation.size;
            })
            .map(({ payload }) => {
                const { collection, mutate } = store.getState();
                const { committedHEADs, uncommittedMutations } = mutate;
                const { sessionId, merchantId } = collection as ICollectionState;
                const { objectId, syncData } = payload;

                const committedHEAD = committedHEADs.get(objectId);

                const operations = mapOperations(uncommittedMutations, objectId);

                const requestId = getAndIncrementRequestId(objectId);
                const revision = parseInt(committedHEAD.revision, 10) + 1;
                const command = {
                    operations,
                    revision,
                    req_id: requestId,
                    syncData: !!syncData,
                };

                store.dispatch({
                    type: MUTATE_OPERATIONS_SENT,
                    payload: {
                        requestId,
                        objectId,
                        revision,
                        objectType: committedHEAD.object_type,
                        sentMutationsIndex: 0,
                    },
                });

                return {
                    command,
                    sessionId,
                    requestId,
                    collectionId: committedHEAD.document_id,
                    objectId,
                    revision,
                    merchantId,
                };
            })
            .concatMap(({ command, collectionId, requestId, sessionId, objectId, merchantId }) => {
                const url = `${siteConfig.goAPI}/v1/collections/${collectionId}/target_groups/mutate?sid=${sessionId}&req_id=${requestId}`;

                return ajax({
                    body: { ...command, merchant_id: merchantId },
                    method: "POST",
                    url,
                })
                    .map(() => syncTargetGroupsCompletedAction({ collectionId }))
                    .catch((error) => handleTargetGroupsSyncError(error, { objectId, collectionId }));
            })
    );
};

// --------------------Target Bag Values-------------------- //

export const bulkMutateTargetBagEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.TARGET_METRICS.BAG_VALUE.BULK_MUTATE)
        .filter(({ payload }) => {
            return payload.values.length > 0;
        })
        .flatMap(({ payload }: { payload: { collectionId: string; values: IMutateAllocationBagValue[] } }) => {
            const { values } = payload;
            const {
                mutate: { snapshots },
            } = store.getState();
            const currentBagValues = snapshots.get(TARGET_BAG_ID);

            const operations = values.map(({ bagType, dimensionValues, quantity, rowId }) => {
                const compoundKey = [rowId, bagType].join(BAG_VALUE_PARAMS_SEPARATOR);

                // If zero quantity and no dimension values specified, we want to clear item's bag
                if (!quantity && dimensionValues.length === 0) {
                    return {
                        type: OperationType.Clear,
                        childKey: compoundKey,
                    };
                }

                const currentValuePath = getAllocationBagValuePath(bagType, rowId, dimensionValues, false);
                let mutatedValue = currentBagValues.getIn(currentValuePath) ?? Map();
                mutatedValue = mutatedValue.set("string_value", (quantity || 0).toString());

                return {
                    type: !quantity ? OperationType.Remove : OperationType.Set,
                    childKey: compoundKey,
                    key: dimensionValues.join(BAG_VALUE_PARAMS_SEPARATOR),
                    value: mutatedValue,
                };
            });

            return [bulkMixedSetOperation(TARGET_BAG_ID, MutableType.Map, operations)];
        });

export const collectionTargetBagValuesSync = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.TARGET_METRICS.BAG_VALUE.SYNC.START)
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();

            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload }) => {
            const { collection, mutate } = store.getState();
            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;
            const { objectId } = payload;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];

            uncommittedMutations.getIn([objectId, 0]).operations.forEach((mutation) => {
                const { childKey, operation } = mutation;
                const [rowId] = childKey.split(BAG_VALUE_PARAMS_SEPARATOR);

                const valuePayload = getAllocationBagMutationPayload(mutation);
                operations.push({
                    [operation]: {
                        target_id: rowId,
                        value: valuePayload,
                    },
                });
            });

            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const command = {
                req_id: requestId,
                revision,
                batches: createBatchMutations(operations, "target_id"),
                merchant_id: merchantId,
            };

            store.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                    sentMutationsIndex: 0,
                },
            });

            return { command, sessionId, requestId, collectionId: committedHEAD.document_id };
        })
        .concatMap(({ collectionId, requestId, sessionId, command }) => {
            if (command.batches.length === 0) {
                return [{ type: COLLECTIONS.COLLECTION.TARGET_METRICS.BAG_VALUE.SYNC.COMPLETE }];
            }
            const url = `/v1/collections/${collectionId}/ref/targets/allocation_bag/mutate?sid=${sessionId}&req_id=${requestId}`;

            return ajax({
                body: command,
                method: "POST",
                url: siteConfig.goAPI + url,
            }).map(() => {
                return { type: COLLECTIONS.COLLECTION.TARGET_METRICS.BAG_VALUE.SYNC.COMPLETE };
            });
        });
};

// --------------------Targets-------------------- //

export const collectionTargetsValuesSync = (action$, store$) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.TARGET_METRICS.TARGETS.SYNC.START)
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store$.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();

            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload }) => {
            const state = store$.getState();
            const { collection, mutate } = state;
            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;
            const { objectId } = payload;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];
            let targetGroupId = undefined;

            // Creating operations objects
            uncommittedMutations.getIn([objectId, 0]).operations.forEach((mutation) => {
                const { operation, value, childKey } = mutation;
                if (!targetGroupId) {
                    targetGroupId = childKey;
                }
                operations.push({
                    [operation]: {
                        value,
                    },
                });
            });

            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const requestBody = {
                operations,
                req_id: requestId,
                revision,
                merchant_id: merchantId,
            };

            store$.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                    sentMutationsIndex: 0,
                },
            });

            const mappedOperations = mapOperations(uncommittedMutations, TARGETS_ID);

            return {
                requestBody: { ...requestBody, operations: mappedOperations },
                sessionId,
                requestId,
                collectionId: committedHEAD.document_id,
                targetGroupId,
            };
        })
        .concatMap(({ collectionId, requestId, sessionId, requestBody, targetGroupId }) => {
            const url = `${siteConfig.goAPI}/v1/collections/${collectionId}/target_groups/${targetGroupId}/mutate?sid=${sessionId}&req_id=${requestId}`;

            return ajax({
                body: requestBody,
                method: "POST",
                url,
            }).map(() => {
                return { type: COLLECTIONS.COLLECTION.TARGET_METRICS.TARGETS.SYNC.COMPLETED };
            });
        });
};

// --------------------Queue-------------------- //
type SaveTargetObjectsObservableAction = ActionsObservable<{
    payload: SaveTargetObjectsPayload;
    type: typeof COLLECTIONS.COLLECTION.TARGET_METRICS.SAVE;
}>;

export const saveTargetObjectsEpic: Epic<Action, IStore> = (action$: SaveTargetObjectsObservableAction, store$) => {
    return action$.ofType(COLLECTIONS.COLLECTION.TARGET_METRICS.SAVE).concatMap(({ payload }) => {
        const state = store$.getState();
        const queueType = state.collection.targetsQueue.type;
        const isCreating = queueType === TargetsQueueType.CREATE;

        const targetsOperations = convertTargetGroupToMutations(
            store$.getState(),
            payload.targetGroupWithTargets,
            payload.targetBag
        );

        const validOperations = targetsOperations.filter((op) => op.length > 0);
        const operations = isCreating
            ? validOperations
            : [[createGhostTargetSetOperations(state, payload.targetGroupWithTargets.id)], ...validOperations];

        return [
            createTargetsQueue({ operations, targetName: payload.queueOptions.targetName }),
            executeTargetsQueueAction(),
        ];
    });
};

export const removeTargetObjectsEpic: Epic<Action, IStore> = (action$: RemoveTargetObservableAction, store$) => {
    return action$.ofType(COLLECTIONS.COLLECTION.TARGET_METRICS.REMOVE).concatMap(({ payload }) => {
        const { queueOptions, ...operationsTarget } = payload;
        const reduxStore = store$.getState();

        const targetsOperations = generateTargetRemoveMutations(operationsTarget, reduxStore);
        const validOperations = targetsOperations.filter((op) => op.length > 0);

        return [
            createTargetsQueue({ operations: validOperations, targetName: payload.queueOptions.targetName }),
            executeTargetsQueueAction(),
        ];
    });
};

const handleSelectNewTarget = (reduxStore: IStore, targetGroupName: string) => {
    const findTargetGroupByName = (targetGroupName: string, reduxStore: IStore) => {
        const targetGroupWithTargets = getTargetGroupsWithTargets(reduxStore);
        return targetGroupWithTargets.find((targetGroup) => targetGroup.name === targetGroupName);
    };

    const { financialTarget } = reduxStore;
    const savedTargetGroup = findTargetGroupByName(targetGroupName, reduxStore);
    if (savedTargetGroup) {
        const selectedTargetIds = savedTargetGroup.targets.reduce((acc, target) => {
            return acc.add(target.id);
        }, financialTarget.selectedTargetIds);

        return updateSelectedTargetIds({ targetIds: selectedTargetIds });
    }

    return;
};

export const executeTargetsQueue: Epic<Action, IStore> = (action$, store$) => {
    return action$.ofType(COLLECTIONS.COLLECTION.TARGET_METRICS.QUEUE.EXECUTE).concatMap(() => {
        const state = store$.getState();
        const { collection } = state;
        const { targetsQueue } = collection as ICollectionState;
        const { operations, type, targetName } = targetsQueue;

        const [nextOperations] = operations;
        const queueHasFinished = !nextOperations;

        if (queueHasFinished) {
            const actions: Array<Action> = [clearTargetsQueueAction()];
            const messageMap = {
                [TargetsQueueType.EDIT]: "modified",
                [TargetsQueueType.CREATE]: "added",
                [TargetsQueueType.REMOVE]: "deleted",
            };
            if (targetName) {
                const messageToFormat = `|targetName| has been ${messageMap[type]}`;
                const message = format(messageToFormat, { targetName });
                store$.dispatch(snackbarAdd({ message, options: { styles: { zIndex: 1000001 } } }));

                // If we are creating a target group, we need to select it
                if (type === TargetsQueueType.CREATE) {
                    const selectNewTargetAction = handleSelectNewTarget(state, targetName);
                    if (selectNewTargetAction) {
                        actions.push(selectNewTargetAction);
                    }
                }
            }

            return actions;
        }

        return [...nextOperations, shiftTargetsQueueAction()];
    });
};

export const handleTargetGroupMutateComplete: Epic<Action, IStore> = (action$) =>
    action$.ofType(COLLECTIONS.COLLECTION.TARGET_METRICS.TARGET_GROUPS.SYNC.COMPLETED).map(() => {
        return executeTargetsQueueAction();
    });

export const handleTargetMutateComplete: Epic<Action, IStore> = (action$) =>
    action$.ofType(COLLECTIONS.COLLECTION.TARGET_METRICS.TARGETS.SYNC.COMPLETED).map(() => {
        return executeTargetsQueueAction();
    });

export const handleTargetBagMutateComplete: Epic<Action, IStore> = (action$) =>
    action$.ofType(COLLECTIONS.COLLECTION.TARGET_METRICS.BAG_VALUE.SYNC.COMPLETE).map(() => {
        return executeTargetsQueueAction();
    });
